import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import AdvancedAnalytics from "./components/AdvancedAnalytics";

const STORAGE_KEYS = {
  botUrls: "site2_bot_urls",
  adminSession: "site2_admin_session",
  adminSecret: "site2_admin_secret"
};

const ADMIN_PASSWORD = "admin123";

const BOT_PRESETS = {
  avrora: {
    title: "Аврора (Telegram)",
    url: "https://api.avrora.sverhum.ru/apiavrora"
  },
  sverhum_vk: {
    title: "Сверхум (VK)",
    url: "https://api.sverhum.sverhum.ru"
  },
  sverhum_tg: {
    title: "Сверхум (Telegram)",
    url: "https://api.sverhum-tg.sverhum.ru/api"
  }
};

const BOT_KEYS = Object.keys(BOT_PRESETS);

const PLAN_TO_METHOD = {
  lite: "start",
  pro: "master",
  premium: "year"
};

const PLAN_LABELS = {
  lite: "Старт",
  pro: "Мастер",
  premium: "Годовой"
};

const BALANCE_PRESETS = [100, 500, 1000, 5000, 10000, 50000];
const DAYS_PRESETS = [7, 14, 30, 90, 180, 365];

function safeNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function formatDateTime(ts) {
  if (!ts) return "-";
  return new Date(ts).toLocaleString("ru-RU");
}

function parseBotUrls() {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.botUrls);
    if (!raw) return Object.fromEntries(BOT_KEYS.map((k) => [k, BOT_PRESETS[k].url]));
    const parsed = JSON.parse(raw);
    return Object.fromEntries(
      BOT_KEYS.map((key) => [key, String(parsed?.[key] || BOT_PRESETS[key].url).trim()])
    );
  } catch {
    return Object.fromEntries(BOT_KEYS.map((k) => [k, BOT_PRESETS[k].url]));
  }
}

function normalizeUser(raw) {
  const premiumObj = raw?.premium && typeof raw.premium === "object" ? raw.premium : null;
  const refObj = raw?.ref && typeof raw.ref === "object" ? raw.ref : null;

  return {
    id: raw?.id ?? null,
    uid: raw?.uid ?? null,
    username: raw?.username || "-",
    firstName: raw?.first_name || "-",
    vkId: raw?.vkId || "-",
    balance: safeNumber(raw?.balance ?? raw?.voice, 0),
    premium: {
      id: safeNumber(premiumObj?.id ?? raw?.premium_id, 0),
      time: premiumObj?.time ?? raw?.premium_time ?? null,
      access: Boolean(premiumObj?.access ?? raw?.premium_access)
    },
    ref: {
      activation: refObj?.activation ?? raw?.ref_activation ?? "-",
      count: safeNumber(refObj?.count ?? raw?.ref_count, 0),
      balance: safeNumber(refObj?.balance ?? raw?.ref_balance, 0),
      rub: safeNumber(refObj?.rub ?? raw?.ref_rub, 0),
      partner: Boolean(refObj?.partner ?? raw?.ref_partner)
    },
    ban: Boolean(raw?.ban ?? raw?.settings?.ban)
  };
}

function extractError(error) {
  return (
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    "Неизвестная ошибка"
  );
}

function card(title, value) {
  return { title, value: safeNumber(value, 0) };
}

function createMailButton(type = "url") {
  return {
    id: `${Date.now()}_${Math.random()}`,
    type,
    text: "",
    value: ""
  };
}

function isHttpUrl(value) {
  return /^https?:\/\/\S+$/i.test(String(value || "").trim());
}

export default function App() {
  const [botUrls, setBotUrls] = useState(parseBotUrls);
  const [isAuthed, setIsAuthed] = useState(localStorage.getItem(STORAGE_KEYS.adminSession) === "1");
  const [adminSecret, setAdminSecret] = useState(localStorage.getItem(STORAGE_KEYS.adminSecret) || "");
  const [passwordInput, setPasswordInput] = useState("");
  const [authStatus, setAuthStatus] = useState("");

  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState("");
  const [dashboardData, setDashboardData] = useState([]);

  const [selectedBot, setSelectedBot] = useState("avrora");
  const [userId, setUserId] = useState("");
  const [userCard, setUserCard] = useState(null);
  const [userLoading, setUserLoading] = useState(false);
  const [userActionLoading, setUserActionLoading] = useState(false);
  const [balanceAmount, setBalanceAmount] = useState("1000");
  const [plan, setPlan] = useState("lite");
  const [days, setDays] = useState("30");

  const [mailBot, setMailBot] = useState("avrora");
  const [mailTarget, setMailTarget] = useState("all");
  const [mailMessage, setMailMessage] = useState("");
  const [mailAttachment, setMailAttachment] = useState("");
  const [mailButtons, setMailButtons] = useState([]);
  const [mailLoading, setMailLoading] = useState(false);

  const [events, setEvents] = useState([]);

  const hasUserId = Boolean(String(userId || "").trim());

  const aggregate = useMemo(() => {
    const initial = {
      totalUsers: 0,
      activeDay: 0,
      activeWeek: 0,
      activeMonth: 0,
      registeredDay: 0,
      registeredWeek: 0,
      registeredMonth: 0,
      paidUsers: 0,
      purchasesMonth: 0
    };

    return dashboardData.reduce((acc, item) => {
      if (!item.summary) return acc;
      const s = item.summary;
      acc.totalUsers += safeNumber(s.totalUsers);
      acc.activeDay += safeNumber(s?.active?.day);
      acc.activeWeek += safeNumber(s?.active?.week);
      acc.activeMonth += safeNumber(s?.active?.month);
      acc.registeredDay += safeNumber(s?.registered?.day);
      acc.registeredWeek += safeNumber(s?.registered?.week);
      acc.registeredMonth += safeNumber(s?.registered?.month);
      acc.paidUsers +=
        safeNumber(s?.subscription?.Start) +
        safeNumber(s?.subscription?.Master) +
        safeNumber(s?.subscription?.Year);
      acc.purchasesMonth +=
        safeNumber(s?.archive?.start?.month) +
        safeNumber(s?.archive?.master?.month) +
        safeNumber(s?.archive?.year?.month);
      return acc;
    }, initial);
  }, [dashboardData]);

  function addEvent(text, isError = false) {
    const item = {
      id: Date.now() + Math.random(),
      text,
      isError,
      date: Date.now()
    };
    setEvents((prev) => [item, ...prev].slice(0, 100));
  }

  function persistBotUrls(nextUrls) {
    localStorage.setItem(STORAGE_KEYS.botUrls, JSON.stringify(nextUrls));
  }

  function cleanUrl(value) {
    return String(value || "").trim().replace(/\/+$/, "");
  }

  function saveApiSettings() {
    const nextUrls = Object.fromEntries(BOT_KEYS.map((key) => [key, cleanUrl(botUrls[key])]));
    setBotUrls(nextUrls);
    persistBotUrls(nextUrls);
    addEvent("URL API ботов сохранены.");
  }

  function loginWithPassword() {
    const value = String(passwordInput || "").trim();
    if (!value) {
      setAuthStatus("Введите пароль.");
      return;
    }
    if (value !== ADMIN_PASSWORD) {
      setAuthStatus("Неверный пароль.");
      addEvent("Неудачная попытка входа.", true);
      return;
    }
    localStorage.setItem(STORAGE_KEYS.adminSession, "1");
    localStorage.setItem(STORAGE_KEYS.adminSecret, value);
    setIsAuthed(true);
    setAdminSecret(value);
    setPasswordInput("");
    setAuthStatus("");
    addEvent("Вход выполнен.");
  }

  function logout() {
    localStorage.removeItem(STORAGE_KEYS.adminSession);
    localStorage.removeItem(STORAGE_KEYS.adminSecret);
    setIsAuthed(false);
    setAdminSecret("");
    setUserCard(null);
    addEvent("Выход из админки.");
  }

  async function callBot(botKey, payload) {
    const url = cleanUrl(botUrls[botKey]);
    if (!url) {
      throw new Error(`Не задан URL для ${BOT_PRESETS[botKey].title}`);
    }
    const { data } = await axios.post(url, payload, { timeout: 30000 });
    return data;
  }

  async function loadDashboard() {
    setDashboardLoading(true);
    setDashboardError("");

    try {
      const rows = await Promise.all(
        BOT_KEYS.map(async (botKey) => {
          try {
            const [summary, archive] = await Promise.all([
              callBot(botKey, {}),
              callBot(botKey, { method: "arhiveData" })
            ]);
            return {
              bot: botKey,
              title: BOT_PRESETS[botKey].title,
              summary,
              archive,
              error: ""
            };
          } catch (error) {
            return {
              bot: botKey,
              title: BOT_PRESETS[botKey].title,
              summary: null,
              archive: null,
              error: extractError(error)
            };
          }
        })
      );

      setDashboardData(rows);
      addEvent("Дашборд обновлен.");
    } catch (error) {
      const message = extractError(error);
      setDashboardError(message);
      addEvent(`Ошибка дашборда: ${message}`, true);
    } finally {
      setDashboardLoading(false);
    }
  }

  async function searchUser() {
    setUserLoading(true);
    try {
      if (!userId.trim()) throw new Error("Введите ID пользователя");
      const raw = await callBot(selectedBot, {
        method: "search",
        id: userId.trim()
      });
      setUserCard(normalizeUser(raw));
      addEvent(`Пользователь ${userId.trim()} загружен из ${BOT_PRESETS[selectedBot].title}.`);
    } catch (error) {
      const message = extractError(error);
      setUserCard(null);
      addEvent(`Ошибка поиска пользователя: ${message}`, true);
    } finally {
      setUserLoading(false);
    }
  }

  async function userAction(type) {
    setUserActionLoading(true);
    try {
      const idValue = userId.trim();
      if (!idValue) throw new Error("Введите ID пользователя");

      let payload = { id: idValue };

      if (type === "add_balance") {
        const amount = safeNumber(balanceAmount);
        if (amount <= 0) throw new Error("Сумма должна быть больше 0");
        payload = { ...payload, method: "givetoken", token: amount };
      }
      if (type === "remove_balance") {
        const amount = safeNumber(balanceAmount);
        if (amount <= 0) throw new Error("Сумма должна быть больше 0");
        payload = { ...payload, method: "taketoken", token: amount };
      }
      if (type === "set_subscription") {
        const mapped = PLAN_TO_METHOD[plan];
        const dayCount = safeNumber(days);
        if (!mapped) throw new Error("Неверный тариф");
        if (dayCount <= 0) throw new Error("Срок должен быть больше 0");
        payload = { ...payload, method: "give", subscription: mapped, day: dayCount };
      }
      if (type === "clear_subscription") {
        payload = { ...payload, method: "take" };
      }
      if (type === "ban") {
        payload = { ...payload, method: "ban" };
      }
      if (type === "unban") {
        payload = { ...payload, method: "unban" };
      }

      await callBot(selectedBot, payload);
      addEvent(`Действие "${type}" выполнено для ${idValue}.`);
      await searchUser();
    } catch (error) {
      addEvent(`Ошибка действия: ${extractError(error)}`, true);
    } finally {
      setUserActionLoading(false);
    }
  }

  function addMailButton(type) {
    setMailButtons((prev) => [...prev, createMailButton(type)]);
  }

  function updateMailButton(id, patch) {
    setMailButtons((prev) => prev.map((btn) => (btn.id === id ? { ...btn, ...patch } : btn)));
  }

  function removeMailButton(id) {
    setMailButtons((prev) => prev.filter((btn) => btn.id !== id));
  }

  function moveMailButton(id, direction) {
    setMailButtons((prev) => {
      const index = prev.findIndex((btn) => btn.id === id);
      if (index < 0) return prev;
      const next = [...prev];
      if (direction === "up" && index > 0) {
        [next[index - 1], next[index]] = [next[index], next[index - 1]];
      }
      if (direction === "down" && index < next.length - 1) {
        [next[index + 1], next[index]] = [next[index], next[index + 1]];
      }
      return next;
    });
  }

  function clearMailingForm() {
    setMailMessage("");
    setMailAttachment("");
    setMailButtons([]);
  }

  async function sendMailing() {
    setMailLoading(true);
    try {
      const message = String(mailMessage || "").trim();
      if (!message) throw new Error("Введите текст рассылки");

      const preparedButtons = mailButtons
        .map((btn) => ({
          type: String(btn.type || "url").toLowerCase() === "callback" ? "callback" : "url",
          text: String(btn.text || "").trim(),
          value: String(btn.value || "").trim()
        }))
        .filter((btn) => btn.text && btn.value);

      const invalidUrlButton = preparedButtons.find((btn) => btn.type === "url" && !isHttpUrl(btn.value));
      if (invalidUrlButton) {
        throw new Error(`URL кнопки должен начинаться с http/https: "${invalidUrlButton.text}"`);
      }

      const firstButton = preparedButtons[0] || null;

      await callBot(mailBot, {
        method: "newsletter",
        message,
        attachment: String(mailAttachment || "").trim(),
        buttonText: firstButton?.text || "",
        buttonUrl: firstButton?.value || "",
        buttons: preparedButtons,
        recipientGroup: mailTarget
      });

      addEvent(`Рассылка запущена в ${BOT_PRESETS[mailBot].title}.`);
    } catch (error) {
      addEvent(`Ошибка рассылки: ${extractError(error)}`, true);
    } finally {
      setMailLoading(false);
    }
  }

  const aggregateCards = [
    card("Всего пользователей", aggregate.totalUsers),
    card("Активных за день", aggregate.activeDay),
    card("Активных за неделю", aggregate.activeWeek),
    card("Активных за месяц", aggregate.activeMonth),
    card("Новых за день", aggregate.registeredDay),
    card("Новых за неделю", aggregate.registeredWeek),
    card("Новых за месяц", aggregate.registeredMonth),
    card("Платных пользователей", aggregate.paidUsers),
    card("Покупок за месяц", aggregate.purchasesMonth)
  ];

  useEffect(() => {
    if (isAuthed && dashboardData.length === 0 && !dashboardLoading) {
      loadDashboard();
    }
  }, [isAuthed]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="admin-root">
      <div className="bg-orb orb-1" />
      <div className="bg-orb orb-2" />
      <div className="content">
        <header className="hero panel">
          {/* <div>
            <h1>site2: Единая админка</h1>
            <p>Управление ботами Аврора и Сверхум в одном интерфейсе.</p>
          </div> */}
          {isAuthed ? (
            <div className="hero-actions">
              <button className="btn btn-secondary" onClick={loadDashboard} disabled={dashboardLoading}>
                {dashboardLoading ? "Обновление..." : "Обновить"}
              </button>
              <button className="btn btn-danger" onClick={logout}>
                Выйти
              </button>
            </div>
          ) : null}
        </header>

        <section className="panel">
          <h2>URL API ботов</h2>
          <div className="grid three">
            {BOT_KEYS.map((key) => (
              <label key={key}>
                <span>{BOT_PRESETS[key].title}</span>
                <input
                  value={botUrls[key]}
                  onChange={(e) => setBotUrls((prev) => ({ ...prev, [key]: e.target.value }))}
                />
              </label>
            ))}
          </div>
          <div className="actions-row">
            <button className="btn btn-secondary" onClick={saveApiSettings}>
              Сохранить URL
            </button>
          </div>
        </section>

        {!isAuthed ? (
          <section className="panel">
            <h2>Вход</h2>
            <p className="muted">
              Введите пароль для входа в админку. Пароль задается в <code>site2/src/App.js</code> в
              константе <code>ADMIN_PASSWORD</code>.
            </p>
            <div className="auth-row">
              <input
                type="password"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                placeholder="Пароль"
                onKeyDown={(e) => {
                  if (e.key === "Enter") loginWithPassword();
                }}
              />
              <button className="btn" onClick={loginWithPassword}>
                Войти
              </button>
            </div>
            {authStatus ? <div className="status">{authStatus}</div> : null}
          </section>
        ) : (
          <>
            <AdvancedAnalytics
              adminToken={adminSecret}
              botUrls={botUrls}
              botPresets={BOT_PRESETS}
            />

            <section className="panel">
              <h2>Общий дашборд</h2>
              {dashboardError ? <p className="error-text">{dashboardError}</p> : null}
              <div className="cards">
                {aggregateCards.map((c) => (
                  <article className="card" key={c.title}>
                    <span>{c.title}</span>
                    <strong>{c.value}</strong>
                  </article>
                ))}
              </div>
              <div className="bots-grid">
                {dashboardData.map((row) => (
                  <article className="bot-card" key={row.bot}>
                    <h3>{row.title}</h3>
                    {row.error ? (
                      <p className="error-text">{row.error}</p>
                    ) : (
                      <ul>
                        <li>Пользователей: {safeNumber(row?.summary?.totalUsers)}</li>
                        <li>Актив за день: {safeNumber(row?.summary?.active?.day)}</li>
                        <li>Актив за неделю: {safeNumber(row?.summary?.active?.week)}</li>
                        <li>Новые за неделю: {safeNumber(row?.summary?.registered?.week)}</li>
                        <li>
                          Платных:{" "}
                          {safeNumber(row?.summary?.subscription?.Start) +
                            safeNumber(row?.summary?.subscription?.Master) +
                            safeNumber(row?.summary?.subscription?.Year)}
                        </li>
                      </ul>
                    )}
                  </article>
                ))}
              </div>
            </section>

            <section className="panel">
              <h2>Управление пользователем</h2>
              <div className="grid three">
                <label>
                  <span>Бот</span>
                  <select value={selectedBot} onChange={(e) => setSelectedBot(e.target.value)}>
                    {BOT_KEYS.map((key) => (
                      <option key={key} value={key}>
                        {BOT_PRESETS[key].title}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>ID пользователя</span>
                  <input
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    placeholder="Например: 123456"
                  />
                </label>
                <label>
                  <span>&nbsp;</span>
                  <button className="btn" onClick={searchUser} disabled={userLoading || !hasUserId}>
                    {userLoading ? "Поиск..." : "Найти"}
                  </button>
                </label>
              </div>

              <pre className="user-preview">
{userCard
  ? `ID: ${userCard.id}
UID: ${userCard.uid ?? "-"}
Имя: ${userCard.firstName}
Username: ${userCard.username}
VK ID: ${userCard.vkId}
Баланс: ${userCard.balance}
Подписка ID: ${userCard.premium.id}
Подписка до: ${userCard.premium.time ? formatDateTime(userCard.premium.time * 1000) : "-"}
Бан: ${userCard.ban ? "Да" : "Нет"}
Реферал активация: ${userCard.ref.activation}
Рефералов: ${userCard.ref.count}
Реф. баланс: ${userCard.ref.balance}
Партнер: ${userCard.ref.partner ? "Да" : "Нет"}`
  : "Пользователь не загружен"}
              </pre>

              <div className="action-block">
                <h3>Баланс</h3>
                <p className="muted compact">
                  Введите сумму токенов и выберите действие. Быстрые пресеты ускоряют ручные начисления.
                </p>
                <div className="quick-chips">
                  {BALANCE_PRESETS.map((value) => (
                    <button
                      type="button"
                      key={`balance-${value}`}
                      className={`chip ${String(value) === String(safeNumber(balanceAmount, 0)) ? "active" : ""}`}
                      onClick={() => setBalanceAmount(String(value))}
                    >
                      {value}
                    </button>
                  ))}
                </div>
                <div className="grid three">
                  <label>
                    <span>Сумма</span>
                    <input
                      type="number"
                      min="1"
                      value={balanceAmount}
                      onChange={(e) => setBalanceAmount(e.target.value)}
                    />
                  </label>
                  <label>
                    <span>&nbsp;</span>
                    <button
                      className="btn btn-secondary"
                      onClick={() => userAction("add_balance")}
                      disabled={!hasUserId || userActionLoading}
                    >
                      + Начислить
                    </button>
                  </label>
                  <label>
                    <span>&nbsp;</span>
                    <button
                      className="btn btn-secondary"
                      onClick={() => userAction("remove_balance")}
                      disabled={!hasUserId || userActionLoading}
                    >
                      - Списать
                    </button>
                  </label>
                </div>
              </div>

              <div className="action-block">
                <h3>Подписка</h3>
                <p className="muted compact">
                  Выберите тариф и срок. Можно быстро выставить типовые периоды.
                </p>
                <div className="quick-chips">
                  {DAYS_PRESETS.map((value) => (
                    <button
                      type="button"
                      key={`day-${value}`}
                      className={`chip ${String(value) === String(safeNumber(days, 0)) ? "active" : ""}`}
                      onClick={() => setDays(String(value))}
                    >
                      {value} дн
                    </button>
                  ))}
                </div>
                <div className="grid three">
                  <label>
                    <span>Тариф</span>
                    <select value={plan} onChange={(e) => setPlan(e.target.value)}>
                      <option value="lite">{PLAN_LABELS.lite}</option>
                      <option value="pro">{PLAN_LABELS.pro}</option>
                      <option value="premium">{PLAN_LABELS.premium}</option>
                    </select>
                  </label>
                  <label>
                    <span>Срок (дней)</span>
                    <input type="number" min="1" value={days} onChange={(e) => setDays(e.target.value)} />
                  </label>
                  <label>
                    <span>&nbsp;</span>
                    <button
                      className="btn btn-secondary"
                      onClick={() => userAction("set_subscription")}
                      disabled={!hasUserId || userActionLoading}
                    >
                      Выдать подписку
                    </button>
                  </label>
                </div>
                <div className="actions-row">
                  <button
                    className="btn btn-secondary"
                    onClick={() => userAction("clear_subscription")}
                    disabled={!hasUserId || userActionLoading}
                  >
                    Снять подписку
                  </button>
                </div>
              </div>

              <div className="action-block">
                <h3>Модерация</h3>
                <p className="muted compact">Быстрые действия для блокировки и разблокировки пользователя.</p>
                <div className="grid two">
                  <button
                    className="btn btn-danger"
                    onClick={() => userAction("ban")}
                    disabled={!hasUserId || userActionLoading}
                  >
                    Забанить
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => userAction("unban")}
                    disabled={!hasUserId || userActionLoading}
                  >
                    Разбанить
                  </button>
                </div>
              </div>
            </section>

            <section className="panel">
              <h2>Рассылка</h2>
              <div className="grid three">
                <label>
                  <span>Бот</span>
                  <select value={mailBot} onChange={(e) => setMailBot(e.target.value)}>
                    {BOT_KEYS.map((key) => (
                      <option key={key} value={key}>
                        {BOT_PRESETS[key].title}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Аудитория</span>
                  <select value={mailTarget} onChange={(e) => setMailTarget(e.target.value)}>
                    <option value="all">Всем</option>
                    <option value="premium">Только платным</option>
                    <option value="test">Тестовая</option>
                  </select>
                </label>
                <label>
                  <span>Вложение (file_id/attachment)</span>
                  <input
                    value={mailAttachment}
                    onChange={(e) => setMailAttachment(e.target.value)}
                    placeholder="Например: photo..., document..., video..."
                  />
                </label>
              </div>

              <label>
                <span>Текст сообщения</span>
                <textarea
                  rows={6}
                  value={mailMessage}
                  onChange={(e) => setMailMessage(e.target.value)}
                  placeholder="Введите текст рассылки"
                />
              </label>

              <div className="mailing-buttons-actions">
                <button type="button" className="btn btn-secondary" onClick={() => addMailButton("url")}>
                  Добавить URL кнопку
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => addMailButton("callback")}>
                  Добавить callback кнопку
                </button>
              </div>

              {mailButtons.length > 0 ? (
                <div className="mailing-buttons">
                  {mailButtons.map((btn, index) => (
                    <div className="mailing-button-item" key={btn.id}>
                      <div className="mailing-button-header">
                        <span>Кнопка #{index + 1}</span>
                        <div className="mailing-button-controls">
                          <button type="button" onClick={() => moveMailButton(btn.id, "up")} disabled={index === 0}>
                            Вверх
                          </button>
                          <button
                            type="button"
                            onClick={() => moveMailButton(btn.id, "down")}
                            disabled={index === mailButtons.length - 1}
                          >
                            Вниз
                          </button>
                          <button type="button" onClick={() => removeMailButton(btn.id)}>
                            Удалить
                          </button>
                        </div>
                      </div>

                      <div className="grid two">
                        <label>
                          <span>Тип</span>
                          <select value={btn.type} onChange={(e) => updateMailButton(btn.id, { type: e.target.value })}>
                            <option value="url">URL</option>
                            <option value="callback">Callback</option>
                          </select>
                        </label>
                        <label>
                          <span>Текст кнопки</span>
                          <input
                            value={btn.text}
                            onChange={(e) => updateMailButton(btn.id, { text: e.target.value })}
                            placeholder="Например: Открыть"
                          />
                        </label>
                      </div>

                      <label>
                        <span>{btn.type === "url" ? "Ссылка (https://...)" : "payload callback"}</span>
                        <input
                          value={btn.value}
                          onChange={(e) => updateMailButton(btn.id, { value: e.target.value })}
                          placeholder={btn.type === "url" ? "https://example.com" : "my_callback_code"}
                        />
                      </label>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="muted">Кнопки не добавлены.</p>
              )}

              <p className="mailing-help">
                Можно добавить несколько кнопок. URL кнопки должны начинаться с <code>http://</code> или{" "}
                <code>https://</code>. Для callback кнопок укажите строку payload.
              </p>

              <div className="actions-row inline-actions">
                <button className="btn" onClick={sendMailing} disabled={mailLoading}>
                  {mailLoading ? "Отправка..." : "Запустить рассылку"}
                </button>
                <button className="btn btn-secondary" onClick={clearMailingForm} disabled={mailLoading}>
                  Очистить форму
                </button>
              </div>
            </section>

            <section className="panel">
              <h2>События</h2>
              <div className="events">
                {events.length === 0 ? (
                  <p className="muted">Событий пока нет.</p>
                ) : (
                  events.map((event) => (
                    <div className={`event ${event.isError ? "error" : "ok"}`} key={event.id}>
                      <span>{formatDateTime(event.date)}</span>
                      <p>{event.text}</p>
                    </div>
                  ))
                )}
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
